/**
 * Google OAuth Hook for JamDung Jobs
 * Docker-compatible implementation using Google Identity Services
 */

import { useEffect, useCallback, useState } from 'react';
import { logDev, logError } from '../utils/loggingUtils';

// Google OAuth configuration
const GOOGLE_CONFIG = {
  // This will be set via environment variable for Docker
  clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
  scope: 'email profile'
};

// Check if Google Client ID is configured
const isGoogleConfigured = () => {
  return GOOGLE_CONFIG.clientId && 
         GOOGLE_CONFIG.clientId !== '' && 
         GOOGLE_CONFIG.clientId !== 'YOUR_GOOGLE_CLIENT_ID';
};

/**
 * Custom hook for Google OAuth integration
 */
export const useGoogleAuth = () => {
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [currentAbortController, setCurrentAbortController] = useState(null);
  const [tokenClient, setTokenClient] = useState(null);

  // Load Google Identity Services script and pre-initialize token client
  useEffect(() => {
    const loadGoogleScript = () => {
      // Check if script is already loaded
      if (window.google?.accounts) {
        setIsGoogleLoaded(true);
        initializeGoogle();
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        logDev('info', 'Google Identity Services script loaded');
        setIsGoogleLoaded(true);
        initializeGoogle();
      };

      script.onerror = (error) => {
        logError('Failed to load Google Identity Services', error, {
          module: 'useGoogleAuth',
          function: 'loadGoogleScript'
        });
      };

      document.head.appendChild(script);
    };

    const initializeGoogle = () => {
      // Only initialize the OAuth2 token client — do NOT call
      // google.accounts.id.initialize() as it triggers broken
      // One Tap / prompt() fallbacks that open a popup missing
      // the response_type parameter.
      if (window.google?.accounts?.oauth2 && isGoogleConfigured()) {
        try {
          const client = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CONFIG.clientId,
            scope: GOOGLE_CONFIG.scope,
            callback: () => {} // Will be overridden per sign-in attempt
          });
          setTokenClient(client);
          logDev('info', 'Google token client pre-initialized');
        } catch (error) {
          logError('Failed to pre-initialize token client', error, {
            module: 'useGoogleAuth',
            function: 'initializeGoogle'
          });
        }
      }
    };

    loadGoogleScript();
  }, []);

  /**
   * Handle Google Sign In via OAuth2 token client popup
   */
  const signInWithGoogle = useCallback(async (onSuccess, onError) => {
    // Prevent multiple simultaneous requests
    if (isRequestInProgress) {
      logDev('warn', 'Google OAuth request already in progress');
      return;
    }

    // Cancel any existing request
    if (currentAbortController) {
      logDev('info', 'Cancelling existing OAuth request');
      currentAbortController.abort();
    }

    // Check if Google OAuth is properly configured
    if (!isGoogleConfigured()) {
      const error = new Error('Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID environment variable.');
      logError('Google OAuth not configured', error, {
        module: 'useGoogleAuth',
        function: 'signInWithGoogle',
        hasClientId: !!GOOGLE_CONFIG.clientId,
        clientIdValue: GOOGLE_CONFIG.clientId ? `${GOOGLE_CONFIG.clientId.substring(0, 20)}...` : 'empty'
      });
      onError?.(error);
      return;
    }

    if (!isGoogleLoaded || !window.google?.accounts?.oauth2) {
      const error = new Error('Google OAuth not loaded');
      logError('Google OAuth not available', error, {
        module: 'useGoogleAuth',
        function: 'signInWithGoogle',
        isGoogleLoaded,
        hasGoogleAPI: !!window.google?.accounts
      });
      onError?.(error);
      return;
    }

    setIsLoading(true);
    setIsRequestInProgress(true);

    // Create new abort controller for this request
    const abortController = new AbortController();
    setCurrentAbortController(abortController);

    // Set a timeout to prevent the request from being locked indefinitely
    const timeoutId = setTimeout(() => {
      logDev('warn', 'Google OAuth request timeout, releasing lock');
      abortController.abort();
      setIsRequestInProgress(false);
      setCurrentAbortController(null);
    }, 30000); // 30 second timeout

    try {
      logDev('info', 'Using Google OAuth2 token client popup');

      if (!tokenClient && !window.google?.accounts?.oauth2) {
        throw new Error('Google OAuth2 token client not available');
      }

      // Use pre-initialized client, or create one as last resort
      let client = tokenClient;
      if (!client) {
        logDev('warn', 'Token client not pre-initialized, creating on demand');
        client = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CONFIG.clientId,
          scope: GOOGLE_CONFIG.scope,
          callback: () => {}
        });
        setTokenClient(client);
      }

      // Override the callback for this sign-in attempt
      client.callback = async (tokenResponse) => {
        try {
          if (tokenResponse.error) {
            throw new Error(tokenResponse.error);
          }

          logDev('info', 'Google token client response received');

          // Use the access token to fetch user info
          const userInfoResponse = await fetch(
            'https://www.googleapis.com/oauth2/v3/userinfo',
            { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
          );
          const userInfo = await userInfoResponse.json();

          onSuccess?.({
            googleToken: tokenResponse.access_token,
            userInfo: {
              email: userInfo.email,
              name: userInfo.name,
              given_name: userInfo.given_name,
              family_name: userInfo.family_name,
              picture: userInfo.picture,
              email_verified: userInfo.email_verified,
              sub: userInfo.sub
            },
            isAccessToken: true
          });
        } catch (error) {
          logError('Token client callback failed', error, {
            module: 'useGoogleAuth',
            function: 'tokenClient.callback'
          });
          onError?.(error);
        } finally {
          clearTimeout(timeoutId);
          setIsLoading(false);
          setIsRequestInProgress(false);
          setCurrentAbortController(null);
        }
      };

      client.requestAccessToken();

    } catch (error) {
      logError('Failed to initiate Google sign in', error, {
        module: 'useGoogleAuth',
        function: 'signInWithGoogle'
      });
      onError?.(error);
      clearTimeout(timeoutId);
      setIsLoading(false);
      setIsRequestInProgress(false);
      setCurrentAbortController(null);
    }
  }, [isGoogleLoaded, isRequestInProgress, currentAbortController, tokenClient]);

  return {
    isGoogleLoaded,
    isLoading: isLoading || isRequestInProgress,
    signInWithGoogle
  };
};

export default useGoogleAuth;
